/**
 * The partner release date: the one place it is set.
 *
 * Read by the Terms page ("Versjon 21 – gjeldende fra …"), the privacy policy
 * ("Sist oppdatert …"), and the member notice email (server/api/admin/
 * terms-notice.js), whose terms apply NOTICE_DAYS later and which cannot be
 * sent before the release date (00:00 Oslo time).
 *
 * Plain CommonJS so the server can require it as well as the web app.
 *
 * An Oslo calendar date, 'YYYY-MM-DD' (Guro confirmed 14 October on 9 October).
 * Set to null, the pages show a placeholder ("[lanseringsdato]") and the notice
 * cannot be sent.
 */
const RELEASE_DATE = '2026-10-14';

// The member notice: the new terms apply this many days after the release...
const NOTICE_DAYS = 30;
// ...unless the day is set outright. Guro, 14 October: 16 November 2026, so
// every member gets a full 30 days from the notice, which goes out after the
// release. The Terms page and the notice both read it.
const NOTICE_DATE = '2026-11-16';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

const isDate = value => typeof value === 'string' && DATE_PATTERN.test(value);

/**
 * "14. oktober 2026" (or "14. oktober" without the year) for an Oslo calendar
 * date 'YYYY-MM-DD'.
 *
 * @param {string} date
 * @param {boolean} [withYear=true]
 * @returns {string|null}
 */
const formatNorwegianDate = (date, withYear = true) => {
  if (!isDate(date)) {
    return null;
  }
  return new Intl.DateTimeFormat('nb-NO', {
    day: 'numeric',
    month: 'long',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
};

/**
 * The calendar date `days` after `date`, both 'YYYY-MM-DD'.
 *
 * @param {string} date
 * @param {number} days
 * @returns {string|null}
 */
const addDays = (date, days) =>
  isDate(date)
    ? new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10)
    : null;

/**
 * The instant 00:00 Europe/Oslo starts on a calendar date, summer or winter
 * time alike.
 *
 * @param {string} date - 'YYYY-MM-DD'
 * @returns {Date|null}
 */
const osloMidnight = date => {
  if (!isDate(date)) {
    return null;
  }
  const utcMidnight = new Date(`${date}T00:00:00Z`);
  // Oslo's offset from UTC at that moment, in minutes (60 or 120).
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Oslo',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(utcMidnight);
  const osloHour = Number(parts.find(p => p.type === 'hour').value) % 24;
  return new Date(utcMidnight.getTime() - osloHour * 60 * 60 * 1000);
};

module.exports = {
  RELEASE_DATE,
  NOTICE_DAYS,
  formatNorwegianDate,
  addDays,
  osloMidnight,
  /** The release date as text, or null while it is unset. */
  releaseDateText: (date = RELEASE_DATE) => formatNorwegianDate(date),
  NOTICE_DATE,
  /** The day the member notice says the new terms apply from, as text:
      NOTICE_DATE when set, otherwise the release date + NOTICE_DAYS. Null
      while the release date is unset. */
  noticeEffectiveDateText: (date = RELEASE_DATE, withYear = true) =>
    isDate(date)
      ? formatNorwegianDate(
          isDate(NOTICE_DATE) ? NOTICE_DATE : addDays(date, NOTICE_DAYS),
          withYear
        )
      : null,
  /** The first moment the member notice may be sent, or null while unset. */
  noticeSendFrom: (date = RELEASE_DATE) => osloMidnight(date),
};
